import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Flame, ArrowRight, Loader2, Clock, CheckCircle, LogIn, LogOut,
  Shield, TrendingUp, Wallet, X,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import KairosisLogo from "@/components/KairosisLogo";
import CompanyFooter from "@/components/CompanyFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ACCENT = "#FF6D00";

function WaitlistModal({ isOpen, onClose, email: initialEmail = "" }) {
  const [email, setEmail] = useState(initialEmail);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (isOpen) setEmail(initialEmail);
  }, [isOpen, initialEmail]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/waitlist/join`, { email, service: "roar" });
      setSuccess(true);
      setMessage(res.data.message);
    } catch (err) {
      setMessage(err.response?.data?.detail || "Failed to join waitlist.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSuccess(false);
    setMessage("");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={handleClose}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />
      <div
        className="relative w-full sm:max-w-md bg-[#0D0D0D] border border-white/10 rounded-t-3xl sm:rounded-3xl p-6 sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={handleClose} className="absolute top-4 right-4 text-[#71717A] hover:text-white">
          <X className="w-5 h-5" />
        </button>
        {!success ? (
          <>
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5" style={{ color: ACCENT }} />
                <h3 className="text-xl font-bold text-white">Join Roar Waitlist</h3>
              </div>
              <p className="text-[#A1A1AA] text-sm">
                Request access to track your allocated capital in real time.
              </p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#FF6D00]/50"
              />
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-xl font-semibold text-black disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ background: `linear-gradient(135deg, ${ACCENT}, #FF9100)` }}
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : "Request Access"}
              </button>
              {message && !success && <p className="text-red-400 text-sm text-center">{message}</p>}
            </form>
          </>
        ) : (
          <div className="text-center py-4">
            <CheckCircle className="w-12 h-12 mx-auto mb-4" style={{ color: ACCENT }} />
            <h3 className="text-xl font-bold text-white mb-2">You're on the list!</h3>
            <p className="text-[#A1A1AA] text-sm mb-6">{message}</p>
            <button onClick={handleClose} className="px-6 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-white font-medium">
              Got it
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function RoarLanding() {
  const navigate = useNavigate();
  const location = useLocation();
  const [signedIn, setSignedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [roarStatus, setRoarStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [showAuth, setShowAuth] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "", name: "" });

  const fetchRoarStatus = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setSignedIn(false);
      setUser(null);
      setRoarStatus(null);
      setStatusLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/roar/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSignedIn(true);
      setUser(JSON.parse(localStorage.getItem("user") || "{}"));
      setRoarStatus(res.data);
    } catch {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setSignedIn(false);
      setUser(null);
      setRoarStatus(null);
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const stored = localStorage.getItem("user");
    if (token && stored) {
      setSignedIn(true);
      setUser(JSON.parse(stored));
    }
    fetchRoarStatus();
  }, [fetchRoarStatus]);

  useEffect(() => {
    if (location.state?.accessDenied) {
      toast.error("Roar dashboard requires waitlist approval and an active allocation.");
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/signup";
      const res = await axios.post(`${API}${endpoint}`, formData);
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.user));
      setSignedIn(true);
      setUser(res.data.user);
      setShowAuth(false);
      toast.success(isLogin ? "Signed in" : "Account created");
      setStatusLoading(true);
      await fetchRoarStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSignOut = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setSignedIn(false);
    setUser(null);
    setRoarStatus(null);
    toast.success("Signed out");
  };

  const accessBadge = () => {
    if (statusLoading) return null;
    if (!signedIn) return null;
    if (roarStatus?.roar_access) {
      return (
        <Badge className="bg-[#FF6D00]/15 text-[#FF9100] border-[#FF6D00]/30 text-[10px] sm:text-xs shrink-0">
          <CheckCircle className="w-3 h-3 mr-1 shrink-0" />
          <span className="hidden sm:inline">Roar Access Active</span>
          <span className="sm:hidden">Active</span>
        </Badge>
      );
    }
    if (roarStatus?.waitlist?.approved && !roarStatus?.has_allocation) {
      return (
        <Badge className="bg-[#007AFF]/15 text-[#007AFF] border-[#007AFF]/30 text-[10px] sm:text-xs shrink-0 max-w-[130px] sm:max-w-none truncate">
          <Clock className="w-3 h-3 mr-1 shrink-0" />
          <span className="hidden sm:inline">Approved — Setup In Progress</span>
          <span className="sm:hidden">Setup pending</span>
        </Badge>
      );
    }
    if (roarStatus?.waitlist?.on_waitlist && roarStatus?.waitlist?.status === "pending") {
      return (
        <Badge className="bg-yellow-500/15 text-yellow-400 border-yellow-500/30 text-[10px] sm:text-xs shrink-0">
          <Clock className="w-3 h-3 mr-1 shrink-0" />
          <span className="hidden sm:inline">Waitlist Pending</span>
          <span className="sm:hidden">Pending</span>
        </Badge>
      );
    }
    return (
      <Badge className="bg-white/5 text-gray-400 border-white/10 text-[10px] sm:text-xs shrink-0">
        <span className="hidden sm:inline">No Roar Access</span>
        <span className="sm:hidden">No access</span>
      </Badge>
    );
  };

  const primaryAction = () => {
    if (statusLoading) {
      return (
        <Button disabled className="w-full sm:w-auto px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg min-h-[48px]">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...
        </Button>
      );
    }
    if (!signedIn) {
      return (
        <Button
          onClick={() => setShowAuth(true)}
          className="w-full sm:w-auto px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg font-semibold text-black min-h-[48px]"
          style={{ background: `linear-gradient(135deg, ${ACCENT}, #FF9100)` }}
        >
          <LogIn className="w-5 h-5 mr-2" /> Sign In to Roar
        </Button>
      );
    }
    if (roarStatus?.roar_access) {
      return (
        <Button
          onClick={() => navigate("/dashboard")}
          className="w-full sm:w-auto px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg font-semibold text-black min-h-[48px]"
          style={{ background: `linear-gradient(135deg, ${ACCENT}, #FF9100)` }}
        >
          View Dashboard <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      );
    }
    if (roarStatus?.waitlist?.approved && !roarStatus?.has_allocation) {
      return (
        <Button disabled className="w-full sm:w-auto px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg bg-[#007AFF]/20 text-[#007AFF] border border-[#007AFF]/30 min-h-[48px]">
          Account Setup In Progress
        </Button>
      );
    }
    if (!roarStatus?.waitlist?.on_waitlist) {
      return (
        <Button
          onClick={() => setShowWaitlist(true)}
          className="w-full sm:w-auto px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg font-semibold text-black min-h-[48px]"
          style={{ background: `linear-gradient(135deg, ${ACCENT}, #FF9100)` }}
        >
          <Clock className="w-5 h-5 mr-2" /> Join Waitlist
        </Button>
      );
    }
    return (
      <Button
        onClick={() => fetchRoarStatus()}
        variant="outline"
        className="w-full sm:w-auto px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg border-[#FF6D00]/40 text-[#FF9100] min-h-[48px]"
      >
        Refresh Status
      </Button>
    );
  };

  return (
    <div className="min-h-[100dvh] min-h-screen bg-[#030303] text-white font-sans overflow-x-hidden">
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 kairosis-aurora opacity-60" />
        <div className="absolute top-[15%] right-[10%] w-80 h-80 bg-[#FF6D00]/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-[20%] left-[5%] w-72 h-72 bg-[#FF9100]/8 rounded-full blur-[90px]" />
      </div>

      <nav className="relative z-50 flex items-center justify-between gap-2 px-4 sm:px-8 py-3 sm:py-4 border-b border-white/[0.06] backdrop-blur-xl bg-black/40">
        <a href="/" className="flex items-center gap-2 sm:gap-3 hover:opacity-90 shrink-0 min-w-0">
          <KairosisLogo size="sm" />
          <span className="text-base sm:text-lg font-bold text-[#FF6D00]">Roar</span>
        </a>
        <div className="flex items-center gap-1.5 sm:gap-3 min-w-0 flex-shrink">
          {signedIn && user && (
            <span className="text-xs sm:text-sm text-gray-400 hidden md:inline truncate max-w-[140px]">
              {user.name || user.email}
            </span>
          )}
          {accessBadge()}
          {signedIn ? (
            <button
              onClick={handleSignOut}
              className="p-2.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 shrink-0"
              aria-label="Sign out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={() => setShowAuth(true)}
              className="px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium border border-[#FF6D00]/30 text-[#FF9100] hover:bg-[#FF6D00]/10 shrink-0 min-h-[40px]"
            >
              Sign In
            </button>
          )}
        </div>
      </nav>

      <main className="relative z-10 max-w-5xl mx-auto px-4 sm:px-8 py-10 sm:py-16 md:py-24 pb-12">
        <div className="text-center mb-10 sm:mb-12">
          <div
            className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border text-xs sm:text-sm font-medium mb-4 sm:mb-6"
            style={{ borderColor: `${ACCENT}40`, color: ACCENT, background: `${ACCENT}10` }}
          >
            <Flame className="w-4 h-4 shrink-0" /> A Kairosis Product
          </div>
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold mb-4 sm:mb-6 leading-tight px-1">
            Track Your{" "}
            <span className="bg-gradient-to-r from-[#FF6D00] to-[#FF9100] bg-clip-text text-transparent">
              Allocated Capital
            </span>
          </h1>
          <p className="text-gray-400 text-sm sm:text-lg max-w-2xl mx-auto mb-8 sm:mb-10 px-1 leading-relaxed">
            See deposits, profits, withdrawals, and your 70% earnings share — updated in real time.
            Same account as Alphabot. One login, full transparency.
          </p>
          <div className="px-1">{primaryAction()}</div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 mt-10 sm:mt-16">
          {[
            { icon: Wallet, title: "Deposits & Withdrawals", desc: "Full ledger of money in and out of your allocation." },
            { icon: TrendingUp, title: "70% Profit Share", desc: "Your earnings calculated automatically from gross profits." },
            { icon: Shield, title: "Verified Trades", desc: "Recent trades synced directly from your managed account." },
          ].map(({ icon: Icon, title, desc }) => (
            <div
              key={title}
              className="p-5 sm:p-6 rounded-2xl bg-[#0A0A0A]/80 border border-white/[0.08] backdrop-blur-sm hover:border-[#FF6D00]/30 transition-colors"
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: `${ACCENT}15` }}>
                <Icon className="w-6 h-6" style={{ color: ACCENT }} />
              </div>
              <h3 className="font-bold text-lg mb-2">{title}</h3>
              <p className="text-gray-400 text-sm">{desc}</p>
            </div>
          ))}
        </div>

        {signedIn && roarStatus && !roarStatus.roar_access && (
          <div className="mt-12 p-6 rounded-2xl bg-[#0A0A0A]/80 border border-[#FF6D00]/20 text-center">
            <p className="text-gray-400 text-sm mb-2">Signed in as <strong className="text-white">{roarStatus.email}</strong></p>
            <p className="text-gray-500 text-xs">
              Alphabot and Roar share the same account. Roar access requires waitlist approval plus an active allocation.
            </p>
          </div>
        )}
      </main>

      <WaitlistModal
        isOpen={showWaitlist}
        onClose={() => setShowWaitlist(false)}
        email={user?.email || formData.email || ""}
      />

      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="bg-[#111111] border-[#27272A] text-white w-[calc(100%-2rem)] max-w-md max-h-[90dvh] overflow-y-auto mx-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {isLogin ? "Welcome Back" : "Create Account"}
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Use your existing Alphabot credentials or create a new account.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAuth} className="space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="roar-name">Name</Label>
                <Input
                  id="roar-name"
                  type="text"
                  required={!isLogin}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-[#1C1C1E] border-[#27272A] text-white mt-1"
                />
              </div>
            )}
            <div>
              <Label htmlFor="roar-email">Email</Label>
              <Input
                id="roar-email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-[#1C1C1E] border-[#27272A] text-white mt-1"
              />
            </div>
            <div>
              <Label htmlFor="roar-password">Password</Label>
              <Input
                id="roar-password"
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="bg-[#1C1C1E] border-[#27272A] text-white mt-1"
              />
            </div>
            <Button
              type="submit"
              disabled={authLoading}
              className="w-full font-semibold text-black"
              style={{ background: `linear-gradient(135deg, ${ACCENT}, #FF9100)` }}
            >
              {authLoading ? "Please wait..." : isLogin ? "Sign In" : "Create Account"}
            </Button>
            <p className="text-center text-sm text-[#A1A1AA]">
              {isLogin ? "Don't have an account?" : "Already have an account?"}
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="ml-2 hover:underline"
                style={{ color: ACCENT }}
              >
                {isLogin ? "Sign up" : "Sign in"}
              </button>
            </p>
          </form>
        </DialogContent>
      </Dialog>

      <CompanyFooter accent="#FF9100" />
    </div>
  );
}
